import type { BrowserOptions, ErrorEvent, Event } from "@sentry/vue";

/**
 * Error messages we never want in Sentry. Each one is either browser noise or
 * a third-party failure that carries no actionable information, and every
 * event counts against the plan quota.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  // Cross-origin script failure: the browser strips message and stack, so
  // there is nothing to act on. Always a third-party script on this site.
  /^Script error\.?$/,
  // A promise rejected with a raw XMLHttpRequest, from any script. It arrives
  // as an unhandled rejection with no stack, so denyUrls cannot catch it. The
  // known source is Optimizely's event dispatcher, which does this when its
  // POST to logx.optimizely.com is blocked or drops (status 0). Our own
  // requests (ofetch, axios) reject with Error objects, never the raw XHR.
  /^\[object XMLHttpRequest\]$/,
  // Benign layout warnings browsers surface as errors.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  // CHEQ fingerprints with navigator.keyboard.getLayoutMap() and never catches
  // the rejection. Chromium rejects it when the site is framed (Guru, Canvas,
  // Optimizely editor). A native rejection has no stack, so denyUrls and
  // isThirdPartyEvent cannot catch it.
  /getLayoutMap\(\) must be called from a top-level browsing context/
];

/**
 * Script sources that are not ours. Sentry drops an error when its top stack
 * frame comes from one of these. `isThirdPartyEvent` catches the cases where a
 * third-party frame is deeper in the stack.
 */
export const SENTRY_DENY_URLS: RegExp[] = [
  // Browser extensions
  /^(chrome|moz|safari|safari-web)-extension:\/\//i,
  // Optimizely
  /cdn\.optimizely\.com/i,
  // Google Tag Manager (server-side container and direct)
  /sstm\.online\.asu\.edu/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  // Cassie / Syrenis cookie banner, and the loader it pulls in (bundles axios),
  // from asu.edu or its cassiecloud.com fallback
  /cassie\.web\.asu\.edu/i,
  /cscript-cdn-use\.(asu\.edu|cassiecloud\.com)/i,
  // InSync chat button
  /dbrd449anfbv4\.cloudfront\.net/i,
  // CHEQ
  /\.sd30591\.asuonline\.asu\.edu/i,
  // Salesforce live agent chat
  /salesforceliveagent\.com/i,
  // WisePops
  /wisepops\.(com|net)/i,
  // Embeds
  /youtube\.com/i,
  /calendly\.com/i
];

/**
 * True when an event has a stack trace and none of its frames come from our
 * own bundle. These are third-party errors that got past `denyUrls` because
 * the top frame was not the third-party one (e.g. an injected script calling
 * into a browser API).
 *
 * Events with no frames at all are kept. That covers httpClient failures and
 * manual `captureException` calls with non-Error values, which we still want.
 */
export const isThirdPartyEvent = (event: Event, firstPartyOrigin: string): boolean => {
  const frames = (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);
  const filenames = frames
    .map((frame) => frame.filename)
    .filter((filename): filename is string => Boolean(filename));

  if (filenames.length === 0) return false;

  return !filenames.some(
    (filename) => filename.startsWith(firstPartyOrigin) || filename.includes("/_nuxt/")
  );
};

/**
 * Adjusts events created by `httpClientIntegration` before they are sent.
 *
 * - Marks them handled. The SDK flags them `handled: false`, which sets the
 *   visitor's session to "crashed" and drags down crash-free sessions in
 *   Release Health, even for an expected 4xx.
 * - Fingerprints them by method, endpoint and status. They have no stack
 *   trace and their message is only "HTTP Client Error with status code: N",
 *   so Sentry would otherwise merge every endpoint with the same status into
 *   one issue. The query string is left out so e.g. each tuition lookup does
 *   not become its own issue.
 *
 * Other events are returned untouched.
 */
export const normalizeHttpClientEvent = <T extends Event>(event: T): T => {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== "http.client") return event;

  exception.mechanism.handled = true;

  const status = event.contexts?.response?.status_code;
  const method = event.request?.method ?? "GET";
  let endpoint = event.request?.url ?? "unknown";
  try {
    const url = new URL(endpoint);
    endpoint = `${url.host}${url.pathname}`;
  } catch {
    // keep the raw value if it is not an absolute URL
  }
  event.fingerprint = ["http-client", method, endpoint, String(status)];

  return event;
};

/**
 * True for an `httpClientIntegration` 400 from the lead API. That is the API
 * rejecting the visitor's input (e.g. BriteVerify failing an email or phone),
 * which the RFI form already shows as a field error. useRfi reports it as a
 * warning grouped by the rejected field names, so the bare status code here
 * adds nothing (PDASUOW-26393).
 */
export const isLeadApiRejection = (event: Event, leadApiPath?: string): boolean => {
  if (!leadApiPath) return false;
  if (event.exception?.values?.[0]?.mechanism?.type !== "http.client") return false;
  if (event.contexts?.response?.status_code !== 400) return false;

  try {
    return new URL(event.request?.url ?? "").pathname === leadApiPath;
  } catch {
    return false;
  }
};

/**
 * Messages browsers (and Vite's preload helper) use when a `/_nuxt/` chunk
 * fails to download: Chrome/Edge, Firefox, Safari, then Vite's CSS preload.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/;

/**
 * Groups chunk load failures into a single warning-level issue.
 *
 * These are almost always client-side: an ad blocker, a dropped connection, a
 * bot, or a page closed mid-load. The chunks themselves are served fine
 * (ASUO-RDS-FKX, ASUO-RDS-JM8). The message carries the chunk URL, so Sentry
 * would otherwise open a new issue per hashed filename. Nuxt already reloads
 * the page when a route chunk fails during navigation.
 *
 * They are kept rather than dropped so a spike after a deploy still shows up.
 * Other events are returned untouched.
 */
export const normalizeChunkLoadEvent = <T extends Event>(event: T): T => {
  const values = event.exception?.values ?? [];
  const isChunkLoadError = values.some(
    (value) => value.value && CHUNK_LOAD_ERROR_PATTERN.test(value.value)
  );
  if (!isChunkLoadError) return event;

  event.fingerprint = ["chunk-load-error"];
  event.level = "warning";

  return event;
};

/**
 * The filtering options passed to `Sentry.init`. Shared so tests run events
 * through exactly what production uses.
 */
export const sentryFilterOptions = (
  firstPartyOrigin: string,
  { leadApiPath }: { leadApiPath?: string } = {}
): Pick<BrowserOptions, "ignoreErrors" | "denyUrls"> & {
  beforeSend: (event: ErrorEvent) => ErrorEvent | null;
} => ({
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  denyUrls: SENTRY_DENY_URLS,
  beforeSend: (event) =>
    isThirdPartyEvent(event, firstPartyOrigin) || isLeadApiRejection(event, leadApiPath)
      ? null
      : normalizeChunkLoadEvent(normalizeHttpClientEvent(event))
});
